import { SaleReference } from './SaleStore';
import { computeContributionMarginRatio } from './SaleItemStore';
import { Address } from './Address';
import { CompanyReference } from './contacts/CompanyStore';
import {
  Instance,
  types,
  SnapshotIn,
  SnapshotOut,
  getRoot,
  IAnyModelType,
  flow,
} from 'mobx-state-tree';
import { modelWithCRUD } from './utils/modelWithCRUD';
import { PurchaseOrderItemReference } from './PurchaseOrderItemStore';
import { CommomMixin } from './Mixins';
import { RootModelType } from './Root';

/**
 * The status of the purchase order should be computed by asking;
 * - __`DRAFT`__ we have not yet sent the purchase order to the supplier
 * - __`OPEN`__ we have sent the purchas order to the supplier OR have updated any status of the lines.
 * - __`FINALIZED`__ we have handled all quantaties of the lines. (all statuses are done...)
 */
export enum PurchaseOrderStatus {
  DRAFT = 'draft',
  OPEN = 'open',
  FINALIZED = 'finalized',
}

export const PurchaseOrder = types
  .compose(
    CommomMixin,
    types.model({
      id: types.union(types.identifier, types.identifierNumber),
      status: types.enumeration<PurchaseOrderStatus>(
        Object.values(PurchaseOrderStatus)
      ),
      purchase_order_number: types.number,

      supplier: types.late(() => CompanyReference),
      delivery_address: Address,
      drop_shipping: types.boolean,
      attention: types.maybeNull(types.string),
      sales_price: types.maybeNull(types.number),
      sales_price_currency: types.optional(types.string, 'EUR'),
      cost_price: types.optional(types.number, 0),
      cost_price_currency: types.optional(types.string, 'EUR'),

      related_sales: types.array(types.late(() => SaleReference)),
      items: types.array(types.late(() => PurchaseOrderItemReference)),
    })
  )
  .named('PurchaseOrder')
  .views((self) => ({
    get contributionMarginRatio(): number {
      return computeContributionMarginRatio(
        self.sales_price || 0,
        self.cost_price
      );
    },
  }));

export const PurchaseOrderStore = modelWithCRUD(
  'purchase-orders',
  PurchaseOrder
)
  .named('PurchaseOrderStore')
  .actions((self) => ({
    placePurchaseOrder: flow(function* placePurchaseOrder(po: IPurchaseOrder) {
      yield self.env.api.put(`${self.apiPath}/${po.id}/change-state/`, {
        status: PurchaseOrderStatus.OPEN,
      });
      return yield self.getItem(po.id, true);
    }),
    sendPurchaseOrder: flow(function* sendPurchaseOrder(
      purchaseOrder: IPurchaseOrder,
      recipients: string[],
      language: string,
      responsible_bcc: boolean
    ) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${purchaseOrder.id}/send/`, {
          recipients,
          language,
          responsible_bcc: responsible_bcc || false,
        })
        .then((res) => res.data);
      yield self.getItem(purchaseOrder.id, true);
      return data;
    }),
    markAllComplete: flow(function* markAllComplete(
      purchaseOrder: IPurchaseOrder
    ) {
      const data = yield self.env.api
        .post(`${self.apiPath}/${purchaseOrder.id}/mark-complete/`, {})
        .then((res) => res.data);

      self.putItem(data);
      const root = getRoot(self) as RootModelType;
      self.invalidateCaches();
      root.purchaseOrderItemStore.invalidateCaches();
      return self.getItem(purchaseOrder.id);
    }),
  }));

export const PurchaseOrderReference = types.reference(
  types.late((): IAnyModelType => PurchaseOrder),
  {
    get(identifier: string, parent): any {
      return (
        getRoot(parent!) as RootModelType
      ).purchaseOrderStore.getItemNaive(identifier);
    },
    set(value) {
      return value.id;
    },
  }
);

export interface IPurchaseOrder extends Instance<typeof PurchaseOrder> {}
export interface IPurchaseOrderSnapshotIn
  extends SnapshotIn<typeof PurchaseOrder> {}
export interface IPurchaseOrderSnapshotOut
  extends SnapshotOut<typeof PurchaseOrder> {}
